// MediaPipe is browser-only, so we use dynamic imports.
// Keep type-only imports to avoid SSR bundling issues.
import type { PoseLandmarker } from '@mediapipe/tasks-vision';
import type { CaptureSequence, Landmark } from '../components/posture-analysis/types';
import {
  applyFrontalWristTowardMidline,
  applyAnkleHeelBlend,
  applyFrontalHipIliacBias,
  applyFrontalShoulderTowardHeadBias,
} from '@/components/posture-analysis/lib/pose-landmark-bias';

import logger from '@/lib/logging';
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

// Lazy load MediaPipe only in browser
let mediaPipeModule: typeof import('@mediapipe/tasks-vision') | null = null;

async function getMediaPipeModule() {
  if (!isBrowser()) {
    throw new Error('MediaPipe can only be used in browser environment');
  }
  if (!mediaPipeModule) {
    // Prefer version-pinned CDN ESM so JS glue and WASM (WASM_BASE) stay aligned in dev + prod.
    try {
      mediaPipeModule = await import(/* @vite-ignore */ MEDIAPIPE_ESM_CDN);
    } catch (cdnImportError) {
      logger.warn(
        'CDN @mediapipe/tasks-vision import failed; falling back to bundled package:',
        cdnImportError,
      );
      mediaPipeModule = await import('@mediapipe/tasks-vision');
    }
  }
  return mediaPipeModule;
}

let poseLandmarkerImage: PoseLandmarker | null = null;
let poseLandmarkerVideo: PoseLandmarker | null = null;

/** Must match `dependencies["@mediapipe/tasks-vision"]` in package.json — avoids WASM/JS glue mismatch (`_malloc is not a function`). */
const MEDIAPIPE_TASKS_VISION_VERSION = '0.10.14';
const WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_TASKS_VISION_VERSION}/wasm`;
/** Fallback when Vite `.vite/deps` cache is missing or stale (same pin as Playwright pose scripts). */
const MEDIAPIPE_ESM_CDN = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_TASKS_VISION_VERSION}/+esm`;

// Model URLs (Google bucket; int8 weights were removed — use float16)
const MODEL_FULL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task";
const MODEL_LITE = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";

// Get model URL based on quality setting
export function getModelUrl(quality: 'full' | 'lite' = 'full'): string {
  return quality === 'lite' ? MODEL_LITE : MODEL_FULL;
}

async function createPoseLandmarker(
  delegate: 'GPU' | 'CPU', 
  modelUrl: string, 
  runningMode: 'IMAGE' | 'VIDEO'
): Promise<PoseLandmarker> {
  const { PoseLandmarker, FilesetResolver } = await getMediaPipeModule();
  const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
  return PoseLandmarker.createFromOptions(vision, {
    baseOptions: { modelAssetPath: modelUrl, delegate },
    runningMode: runningMode,
    numPoses: 1,
  });
}

async function initializeLandmarker(
  runningMode: 'IMAGE' | 'VIDEO',
  onProgress?: (text: string, sub: string) => void
): Promise<PoseLandmarker> {
  const safeProgress = onProgress ?? (() => {});
  // Return existing instance if available
  if (runningMode === 'IMAGE' && poseLandmarkerImage) return poseLandmarkerImage;
  if (runningMode === 'VIDEO' && poseLandmarkerVideo) return poseLandmarkerVideo;
  if (!isBrowser()) {
    throw new Error('PoseLandmarker can only be initialized in browser environment');
  }

  const start = performance.now();
  const tick = setInterval(() => {
    const s = Math.round((performance.now() - start) / 1000);
    const subText = s < 10 ? `Initializing (${s}s)…` : `GPU slow (${s}s)… CPU fallback`;
    safeProgress(`Loading AI Vision (${runningMode})…`, subText);
  }, 1000);

  try {
    let landmarker: PoseLandmarker;
    try {
      landmarker = await createPoseLandmarker('GPU', MODEL_FULL, runningMode);
    } catch (gpuError) {
      logger.warn('GPU delegate failed, falling back to CPU:', gpuError);
      safeProgress('GPU failed, trying CPU...', 'This may be slower.');
      try {
        landmarker = await createPoseLandmarker('CPU', MODEL_LITE, runningMode);
      } catch (liteError) {
        logger.warn('CPU + lite model failed, trying full float16 model:', liteError);
        safeProgress('Loading larger vision model…', 'One moment.');
        landmarker = await createPoseLandmarker('CPU', MODEL_FULL, runningMode);
      }
    }

    // Cache the instance
    if (runningMode === 'IMAGE') {
      poseLandmarkerImage = landmarker;
    } else {
      poseLandmarkerVideo = landmarker;
    }
    
    return landmarker;
  } catch (error) {
    logger.error(`Failed to initialize PoseLandmarker (${runningMode}):`, error);
    throw new Error(`Could not initialize AI vision model (${runningMode}).`);
  } finally {
    clearInterval(tick);
  }
}

export async function initPoseLandmarker(
    onProgress?: (text: string, sub: string) => void
): Promise<PoseLandmarker> {
  // Default to IMAGE mode for backward compatibility
  return initializeLandmarker('IMAGE', onProgress);
}

export async function initPoseLandmarkerForImage(
    onProgress?: (text: string, sub: string) => void
): Promise<PoseLandmarker> {
  return initializeLandmarker('IMAGE', onProgress);
}

export async function initPoseLandmarkerForVideo(
    onProgress?: (text: string, sub: string) => void
): Promise<PoseLandmarker> {
  return initializeLandmarker('VIDEO', onProgress);
}

function getLandmarksFromResult(result: any): any[] | null {
    const landmarks = (result.landmarks || result.poseLandmarks || [])[0] || null;
    return landmarks;
}

function normalizeAndCleanLandmarks(
  landmarksRaw: any[], 
  imgWidth: number, 
  imgHeight: number,
  visibilityThreshold: number = 0.35
): Landmark[] {
    if (!landmarksRaw) return [];
    
    const cleanedLandmarks: Landmark[] = [];
    for (const p of landmarksRaw) {
        if (!p || (p.visibility ?? 1) < visibilityThreshold) {
            cleanedLandmarks.push(null as any); // Keep index alignment
        } else {
            // Create a new, plain object
            cleanedLandmarks.push({
                x: p.x * imgWidth,
                y: p.y * imgHeight,
                z: p.z,
                visibility: p.visibility,
            });
        }
    }
    return cleanedLandmarks;
}

export interface DetectionOptions {
  visibilityThreshold?: number;
  /**
   * Capture view. Anterior: wrist X nudged toward shoulder midline.
   * Posterior: hips that sit too medial (glute-mass drift) are snapped toward iliac width.
   */
  view?: CaptureSequence;
}

export async function detectPoseInImage(
  landmarker: PoseLandmarker,
  imageDataUrl: string,
  clientHeightCM: number,
  options: DetectionOptions = {}
): Promise<{ landmarks: Landmark[], rawLandmarks: Landmark[], cmPerPx: number, imageWidth: number, imageHeight: number }> {
  const { visibilityThreshold = 0.35 } = options;
  if (!isBrowser()) {
    throw new Error('Pose detection can only be performed in browser environment');
  }
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = imageDataUrl;
  });

  let result = landmarker.detect(img);
  let landmarksRaw = getLandmarksFromResult(result);

  if (!landmarksRaw) {
    const c = document.createElement('canvas');
    const maxH = 1280;
    const s = Math.min(1, maxH / img.height);
    c.width = img.width * s;
    c.height = img.height * s;
    c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
    result = landmarker.detect(c);
    landmarksRaw = getLandmarksFromResult(result);
  }

  if (!landmarksRaw) {
    throw new Error('No person detected in the image.');
  }

  const landmarks = normalizeAndCleanLandmarks(landmarksRaw, img.width, img.height, visibilityThreshold);

  if (options.view === 'Anterior') {
    applyFrontalWristTowardMidline(landmarks, img.width);
  }
  if (options.view === 'Anterior' || options.view === 'Posterior') {
    applyFrontalHipIliacBias(landmarks, options.view);
    applyFrontalShoulderTowardHeadBias(landmarks);
  }

  // Create a deep copy of clean landmarks before the ankle blend
  const rawLandmarks = JSON.parse(JSON.stringify(landmarks));

  // Apply ankle-heel blend by default
  applyAnkleHeelBlend(landmarks);

  const ys = landmarks.filter(Boolean).map(p => p!.y);
  if (ys.length < 2) throw new Error('Could not determine body height in pixels.');
  const heightPx = Math.max(...ys) - Math.min(...ys);
  if (!isFinite(heightPx) || heightPx <= 0) throw new Error('Scale calculation failed (invalid height).');
  
  const cmPerPx = clientHeightCM / heightPx;

  return { landmarks, rawLandmarks, cmPerPx, imageWidth: img.width, imageHeight: img.height };
}
